/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { Component, createRef } from "react";
import { createPortal } from "react-dom";
import { arrayOf, element, bool, string, node } from "prop-types";
import classNames from "classnames";

import Button from "pages/_components/Button";

class Dropdown extends Component {
    node = createRef();

    menuRef = createRef();

    static propTypes = {
        ariaLabel: string,
        block: bool,
        bsStyle: string,
        buttonClass: string,
        children: arrayOf(element).isRequired,
        className: string,
        disabled: bool,
        dropdownButtonContent: node,
        fetching: bool,
        id: string,
        image: string,
        inverse: bool,
        label: string,
        labelClassName: string,
        maxContentWidth: bool,
        positionRelative: bool,
        pullCenter: bool,
        pullLeft: bool,
        pullRight: bool,
        pullUp: bool,
        tabIndex: string,
    };

    static defaultProps = {
        ariaLabel: null,
        block: false,
        bsStyle: null,
        buttonClass: null,
        className: null,
        disabled: false,
        dropdownButtonContent: null,
        fetching: false,
        id: null,
        image: null,
        inverse: false,
        label: null,
        labelClassName: null,
        maxContentWidth: false,
        positionRelative: false,
        pullCenter: false,
        pullLeft: false,
        pullRight: false,
        pullUp: false,
        tabIndex: null,
    };

    state = {
        isOpen: false,
        navIndex: 0,
        menuPosition: null,
    };

    componentDidMount() {
        document.addEventListener("mousedown", this.handleClick, false);
        document.addEventListener("touchstart", this.handleClick, false);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.isOpen && (!prevState.isOpen || prevProps.fetching !== this.props.fetching)) {
            this.updateMenuPosition();
            window.addEventListener("resize", this.updateMenuPosition);
            window.addEventListener("scroll", this.updateMenuPosition, true);
        }

        if (!this.state.isOpen && prevState.isOpen) {
            window.removeEventListener("resize", this.updateMenuPosition);
            window.removeEventListener("scroll", this.updateMenuPosition, true);
        }
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleClick, false);
        document.removeEventListener("touchstart", this.handleClick, false);
        window.removeEventListener("resize", this.updateMenuPosition);
        window.removeEventListener("scroll", this.updateMenuPosition, true);
    }

    getTriggerRect = () => {
        const root = this.node.current;
        if (!root) {
            return null;
        }

        const trigger = root.querySelector("button, a.btn");
        return (trigger || root).getBoundingClientRect();
    };

    updateMenuPosition = () => {
        const rect = this.getTriggerRect();
        if (!rect) {
            return;
        }

        const { pullRight, pullLeft, pullUp, pullCenter, pullLeftWidget } = this.props;
        const gap = 4;
        const menuPosition = {};

        if (pullLeft || pullLeftWidget) {
            menuPosition.top = rect.top;
            menuPosition.right = window.innerWidth - rect.left + gap;
        } else if (pullUp) {
            menuPosition.bottom = window.innerHeight - rect.top + gap;
            if (pullRight) {
                menuPosition.right = window.innerWidth - rect.right;
            } else {
                menuPosition.left = rect.left;
            }
        } else if (pullCenter) {
            menuPosition.bottom = window.innerHeight - rect.bottom;
            menuPosition.left = rect.left;
        } else {
            menuPosition.top = rect.bottom + gap;
            if (pullRight) {
                menuPosition.right = window.innerWidth - rect.right;
            } else {
                menuPosition.left = rect.left;
            }
        }

        this.setState({ menuPosition });
    };

    handleClick = (e) => {
        const { target } = e;
        const { isOpen } = this.state;
        const isInsideTrigger = this.node.current?.contains(target);
        const isInsideMenu = this.menuRef.current?.contains(target);

        if (!isInsideTrigger && !isInsideMenu && isOpen) {
            this.toggleOpen(e);
        }
    };

    toggleOpen = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        this.setState((prevState) => ({ isOpen: !prevState.isOpen, navIndex: 0 }));
    };

    onChildFocus = (param) => {
        this.setState({ navIndex: param });
    };

    onFocusHandler = () => {
        this.setState({ isOpen: true });
    };

    onBlurHandler = (ix) => {
        this.setState({ isOpen: !ix });
    };

    renderMenu = (keyPressHandler, children) => {
        const {
            maxContentWidth,
            positionRelative,
            pullCenter,
            pullLeft,
            pullRight,
            pullUp,
            pullLeftWidget,
        } = this.props;
        const { menuPosition } = this.state;

        return (
            <ul
                ref={this.menuRef}
                style={menuPosition || undefined}
                className={classNames("dropdown__menu", {
                    "dropdown__menu--left": pullLeft,
                    "dropdown__menu--right": pullRight,
                    "dropdown__menu--up": pullUp,
                    "dropdown__menu--center": pullCenter,
                    "dropdown__menu--left-widget": pullLeftWidget,
                    "pos-relative": positionRelative,
                    "dropdown-width-max-content": maxContentWidth,
                })}>
                {React.Children.map(children, (child, ix) => {
                    if (child) {
                        return (
                            <li
                                key={ix}
                                onBlur={() => this.onBlurHandler(ix === children?.length - 1)}
                                className="dropdown__item"
                                onClick={() => {
                                    this.setState({ isOpen: false });
                                }}
                                onKeyDown={keyPressHandler}>
                                {child &&
                                    React.cloneElement(child, {
                                        ...child.props,
                                        ix,
                                        keyPressHandler,
                                        onFocus: () => {
                                            this.onChildFocus(ix);
                                        },
                                    })}
                            </li>
                        );
                    }
                    return null;
                })}
            </ul>
        );
    };

    render() {
        const {
            ariaLabel,
            block,
            bsStyle,
            buttonClass,
            children,
            className,
            disabled,
            dropdownButtonContent,
            fetching,
            id,
            image,
            inverse,
            label,
            labelClassName,
            tabIndex,
        } = this.props;
        const { isOpen, navIndex } = this.state;
        const childrenLength = React.Children.toArray(children).length;

        const keyPressHandler = (ev) => {
            let aux = navIndex;
            if (ev.shiftKey && ev.key === "Tab") {
                aux -= 1;
            } else if (ev.key === "Tab") {
                aux += 1;
            }

            if (aux >= childrenLength || aux < 0) {
                this.setState({ isOpen: false });
            }
        };

        const menuLayer =
            isOpen &&
            !fetching &&
            createPortal(
                <div className="dropdown-portal">{this.renderMenu(keyPressHandler, children)}</div>,
                document.body,
            );

        return (
            <div id={id} className={classNames("dropdown", className, { "dropdown--open": isOpen })} ref={this.node}>
                <Button
                    {...(tabIndex && { tabIndex })}
                    aria-expanded={isOpen}
                    aria-haspopup
                    ariaLabel={ariaLabel}
                    block={block}
                    bsStyle={bsStyle}
                    className={buttonClass}
                    disabled={disabled}
                    expandable
                    image={image}
                    label={label}
                    labelClassName={labelClassName}
                    loading={fetching}
                    onClick={this.toggleOpen}
                    inverse={inverse}
                    onKeyDown={keyPressHandler}>
                    {dropdownButtonContent}
                </Button>
                {menuLayer}
            </div>
        );
    }
}

export default Dropdown;
